/**
 * useFlag — React-хук для проверки feature flag в любом компоненте.
 *
 * Backend хранит flags в `feature_flags` таблице (Postgres) с Redis-кэшем 60с.
 * Endpoint `/api/v1/flags/` возвращает массив всех flags (видимых текущему юзеру).
 *
 * Frontend кэширует ответ в TanStack Query (60с staleTime, 5 мин gcTime),
 * чтобы один `flags.list()` обслуживал все хуки на странице.
 *
 * Использование:
 *
 *     import { useFlag } from '@/hooks/useFlag'
 *
 *     function NewKpiDashboard() {
 *         const { enabled, isLoading } = useFlag('kpi_module')
 *         if (isLoading) return <Skeleton />
 *         if (!enabled) return null
 *         return <FullKpiDashboard />
 *     }
 *
 * Когда использовать flag:
 *   - Запускаем фичу постепенно (rollout_percentage 10% → 50% → 100%)
 *   - Хотим включить у конкретного юзера/роли (allowed_roles="admin,owner")
 *   - Хотим быстрый rollback БЕЗ git revert (toggle через admin UI)
 *
 * Когда НЕ использовать flag (использовать ProtectedRoute / require_role вместо):
 *   - RBAC permission модуля (это про право, не feature) — `module_seed.allowed_roles`
 *   - Pure-public route — `AuthGate` bypass в `main.jsx`
 */

import { useQuery } from '@tanstack/react-query'

import { flags } from '@/api/flags'

const FLAGS_QUERY_KEY = ['flags', 'all']

/**
 * Получить список всех flag'ов (один раз на сессию TanStack кэша).
 * Все useFlag(key) делят один запрос.
 */
export function useAllFlags() {
    return useQuery({
        queryKey: FLAGS_QUERY_KEY,
        queryFn: async () => {
            const response = await flags.list()
            return response?.flags ?? []
        },
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    })
}

/**
 * Проверить конкретный flag.
 *
 * @param {string} flagKey — ключ из feature_flags таблицы
 * @returns {{enabled: boolean, isLoading: boolean, isError: boolean}}
 */
export function useFlag(flagKey) {
    const { data: allFlags, isLoading, isPending, isError, refetch } = useAllFlags()
    const unresolved = isLoading || Boolean(isPending && !allFlags)

    if (unresolved || isError || !allFlags) {
        return { enabled: false, isLoading: unresolved, isError, refetch }
    }

    const flag = allFlags.find((f) => f.key === flagKey)
    return {
        enabled: Boolean(flag?.enabled),
        isLoading: false,
        isError: false,
        refetch,
    }
}
